import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { SliderType, StepperType } from "../timeline-proptypes";
import Typography from "../../../common-components/Typography";
import { STATIC_PATH } from "../../../constants/config";
import { getAudioSource } from "../../../helper/URLHelper";
import { isImageOnly, isTextOnly, isVideoOnly, pxToStep2 } from "../timeline-helper";
import { RULER_OPTIONS, SLIDER_TYPES } from "../timeline-constants";
import { WaveformManagerContext } from "../timeline-waveformmanager";
import vmTheme from "../../../constants/theme";
import { font } from "../../../constants/font";
import { TimelineScrollContext } from "../contexts/timeline-scroll-context";
import { BgContainer, Text, TextContainer, ThumbstripStyled, ThumbWrapper1, ThumbWrapper2 } from "../slider/timeline-slider-components";
import CleaningAudioLoader from "./cleaning-audio-loader";

class WaveFormComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      waveformPath: "",
    };

    this.unmount = false;

    this.getPathId = this.getPathId.bind(this);
    this.setWaveFormPath = this.setWaveFormPath.bind(this);
    this.handleWaveForm = this.handleWaveForm.bind(this);
  }

  componentDidMount() {
    const pathId = this.getPathId(this.props);
    if (pathId) {
      this.waveformTimer = setTimeout(() => {
        this.handleWaveForm({
          slider: this.props.slider,
          sliderData: this.props.sliderData,
          sliderHeight: this.props.sliderHeight,
          pathId,
        });
      }, 100);
    }
  }

  componentDidUpdate(prevProps) {
    const { sliderData, sliderHeight, slider } = this.props;

    const pathId = this.getPathId(this.props);
    const prevPathId = this.getPathId(prevProps);

    if (prevPathId !== pathId && pathId) {
      clearTimeout(this.waveformTimer);
      this.waveformTimer = setTimeout(() => {
        this.handleWaveForm({
          slider,
          sliderData,
          sliderHeight,
          pathId,
        });
      }, 1000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.waveformTimer);
    this.unmount = true;
  }

  // eslint-disable-next-line react/sort-comp
  getPathId(props) {
    const { sliderData, stepSizePx, sliderHeight, slider, utilityWorker } = props;
    let pathId = "";

    if (utilityWorker) {
      const { src } = getAudioSource({
        item: {
          type: sliderData.get("type"),
          subType: sliderData.get("subType"),
          src: sliderData.get("src"),
        },
      });
      pathId = `${src}${stepSizePx}${sliderHeight}${slider.sliderType}`;
    }

    return pathId;
  }

  /**
   * @param {object} params
   * @param {Slider} params.slider
   * @param {number} params.sliderHeight
   * @param {string | undefined} params.pathId
   * @param {object} params.pcmItem
   * @param {boolean | undefined} params.throttlePathUpdate
   */
  async setWaveFormPath(params = {}) {
    const { pathId, pcmItem, slider, sliderHeight } = params;

    try {
      if (!this.props.requestWaveformPath) {
        return;
      }

      const pathResult = await this.props.requestWaveformPath({
        pathId,
        pcmItem,
        slider,
        sliderHeight,
      });

      if (this.unmount) {
        return;
      }

      const recentPathId = this.getPathId(this.props); // check recent props before setting
      if (pathResult.pathId === recentPathId) {
        this.setState({ waveformPath: pathResult.path });
      }
    } catch (error) { }
  }

  /**
   * @param {object} params
   * @param {object} params.sliderData
   * @param {Slider} params.slider
   * @param {number} params.sliderHeight
   * @param {string | undefined} params.pathId
   * @param {boolean | undefined} params.throttlePathUpdate
   */
  handleWaveForm(params = {}) {
    const { sliderData, slider, sliderHeight, pathId, throttlePathUpdate } =
      params;

    const { src } = getAudioSource({
      item: {
        type: sliderData.get("type"),
        subType: sliderData.get("subType"),
        src: sliderData.get("src"),
      },
    });
    if (this.props.getPCMData) {
      this.props
        .getPCMData(src)
        .then((pcmItem) => {
          this.setWaveFormPath({
            pathId,
            pcmItem,
            slider,
            sliderHeight,
            throttlePathUpdate,
          });
        })
        .catch(() => { });
    }
  }

  render() {
    const { isDragSlider, isThumbDrag, audioWaveColor, slider, sliderHeight } =
      this.props;
    const { waveformPath } = this.state;

    const { enterStart, exitEnd } = slider;
    let enterStartPosition = enterStart.position;
    let exitEndPosition = exitEnd.position;
    const { mediaMin } = enterStart;

    if (isThumbDrag && isDragSlider) {
      if (enterStart.dragPosition || enterStart.trackPosition) {
        enterStartPosition =
          enterStart.dragPosition || enterStart.trackPosition;
      }
      if (exitEnd.dragPosition || exitEnd.trackPosition) {
        exitEndPosition = exitEnd.dragPosition || exitEnd.trackPosition;
      }
    }

    const svgPlot = {
      x: enterStartPosition.x - mediaMin.x,
      y: 0,
      width: exitEndPosition.x - enterStartPosition.x,
      height: sliderHeight,
    };

    const svgViewBox = `${svgPlot.x} ${svgPlot.y} ${svgPlot.width} ${svgPlot.height}`;

    return (
      <svg
        viewBox={svgViewBox}
        style={{
          width: `${svgPlot.width}px`,
          height: `${svgPlot.height}px`,
          position: "absolute",
          left: 0,
          top: 0,
        }}
      >
        {waveformPath && <path d={waveformPath} fill={audioWaveColor} />}
      </svg>
    );
  }
}
WaveFormComponent.propTypes = {
  utilityWorker: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
  isDragSlider: PropTypes.bool,
  isThumbDrag: PropTypes.bool,
  audioWaveColor: PropTypes.string,
  slider: SliderType,
  sliderHeight: PropTypes.number,
  sliderData: PropTypes.object,
  stepSizePx: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
  getPCMData: PropTypes.func,
  requestWaveformPath: PropTypes.func,
};

const WaveForm = (props) => {
  const utilityWorker = useSelector((state) =>
    state.app.getIn(["workers", "UTILITY_1"])
  );
  const managerCallbacks = useContext(WaveformManagerContext);

  return (
    <WaveFormComponent
      {...props}
      getPCMData={managerCallbacks.getPCMData}
      requestWaveformPath={managerCallbacks.requestWaveformPath}
      utilityWorker={utilityWorker}
    />
  );
};

const ThumbstripComponent = (props) => {
  const {
    item,
    timelinePlotWidth,
    slider,
    sliderHeight,
    stepSizePx,
    stepperInterval,
    isDragSlider,
    thumbstripSrc,
    thumbSrc
  } = props;
  const { enterStart, exitEnd, itemType, itemSubType } = slider;

  const timelineScroll = useContext(TimelineScrollContext);
  const timelineScrollX = timelineScroll.x;

  let enterStartStep = enterStart.position.step;
  let exitEndStep = exitEnd.position.step;
  let mediaMinStep = enterStart.mediaMin
    ? enterStart.mediaMin.step
    : enterStartStep;
  let mediaMaxStep = exitEnd.mediaMax ? exitEnd.mediaMax.step : exitEndStep;
  let enterStartDragStepDiff = 0;
  let exitEndDragStepDiff = 0;

  let mediaDurationSteps = exitEndStep - enterStartStep;
  if (
    isVideoOnly(itemType, itemSubType) &&
    itemType !== "PEXELS" &&
    itemType !== "PIXABAY"
  ) {
    // For videos with thumbstrips, mediaDurationSteps should represent the actual video portion being used
    // This is especially important after split operations where videoStart/videoEnd change
    const videoStart = item.get("videoStart") || 0;
    const videoEnd = item.get("videoEnd") || 0;
    const videoDurationSeconds = videoEnd - videoStart;
    const speed = Number.isFinite(item.get("speed")) ? item.get("speed") : 1;

    const timelineDurationSeconds = videoDurationSeconds / speed;
    mediaDurationSteps = Math.round(timelineDurationSeconds / stepperInterval);

    // Update mediaMinStep and mediaMaxStep to reflect the current video bounds
    mediaMinStep = enterStartStep;
    mediaMaxStep = enterStartStep + mediaDurationSteps;

  } else {
    // pexels and pixabay videos will not have thumbstrip
    mediaMinStep = enterStartStep;
    mediaMaxStep = exitEndStep;
  }

  if (isDragSlider) {
    const enterStartDragPosition =
      enterStart.dragPosition || enterStart.trackPosition;
    const exitEndDragPosition = exitEnd.dragPosition || exitEnd.trackPosition;
    if (enterStartDragPosition) {
      enterStartDragStepDiff = enterStartDragPosition.step - enterStartStep;
      enterStartStep = enterStartDragPosition.step;
    }
    if (exitEndDragPosition) {
      exitEndDragStepDiff = exitEndDragPosition.step - exitEndStep;
      exitEndStep = exitEndDragPosition.step;
    }
    if (enterStartDragPosition && exitEndDragPosition) {
      // slider drag
      mediaMinStep += enterStartDragStepDiff;
      mediaMaxStep += exitEndDragStepDiff;
    }
  }

  const result = useMemo(() => {
    const result = {
      maxThumbCount: null,
      THUMBSTRIP_WIDTH: 90, // width of thumb to show on screen (thumbstrip's actual width will vary)
      THUMBSTRIP_HEIGHT: 50, // height of thumb in thumbstrip
    };
    if (item) {
      const thumb = item.get("thumb");
      if (
        ((item.get("type") !== "STOCKVID" || thumb?.startsWith("https://")) &&
        item.get("type") !== "UPVIDEO" &&
        item.get("type") !== "VIDEO") ||
        (!item.get("thumbstrip") && item.get("type") !== "STOCKVID" && !thumbstripSrc)
      ) {
        result.maxThumbCount = 1;
        result.THUMBSTRIP_HEIGHT = sliderHeight;
      }
      let width = item.get("width");
      let height = item.get("height");
      if (Number.isFinite(item.get("pwidth"))) {
        width = item.get("pwidth");
        height = item.get("pheight");
      }
      result.THUMBSTRIP_WIDTH = Math.floor(
        result.THUMBSTRIP_HEIGHT * (width / height)
      );
      result.speed = Number.isFinite(item.get("speed")) ? item.get("speed") : 1;
      // For aiReframe items, reuse the source video's full thumbstrip and show a
      // TIME-BASED strip (one frame per timeline position) with the reframe crop
      // applied - instead of a single repeated frame. maxThumbCount is left
      // untouched here so the normal per-time frame stepping runs. It stays 1 only
      // when there is no real thumbstrip (handled above), which correctly falls
      // back to a single cropped thumbnail.
      const isReframeItem =
        item.get("type") === "AI_REFRAME" ||
        item.get("isReframe") === true;
      if (isReframeItem) {
        result.THUMBSTRIP_HEIGHT = sliderHeight;
        result.THUMBSTRIP_WIDTH = Math.floor(
          result.THUMBSTRIP_HEIGHT * (width / height)
        );
      }
    }
    return result;
  }, [item, thumbstripSrc, sliderHeight]);
  const { speed, maxThumbCount, THUMBSTRIP_WIDTH, THUMBSTRIP_HEIGHT } =
    result;

  const thumbStyle = useMemo(() => {
    const thumbStyle = {
      backgroundPosition: "",
      backgroundImage: "",
      backgroundSize: "",
    };
    // Extract aiReframe crop data from item's "original" property
    const original = item?.get ? item.get("original") : null;
    const origX = original ? (original.get ? original.get("x") : original.x) : 0;
    const origWidth = original ? (original.get ? original.get("width") : original.width) : 1;
    const origY = original ? (original.get ? original.get("y") : original.y) : 0;
    const isReframeItem =
      item?.get("type") === "AI_REFRAME" ||
      item?.get("isReframe") === true;

    // Natural per-frame dimensions of the generated/served thumbstrip sprite
    // (see generateVideoThumbstrip.js). Frames are stacked vertically.
    const NATURAL_THUMB_WIDTH = 90;
    const NATURAL_THUMB_HEIGHT = 50;
    // Reframe crop zooms the strip horizontally to reframeScaledWidth. Because we
    // scale with "<reframeScaledWidth>px auto", the sprite scales uniformly, so
    // each stacked frame is revealed every reframeFrameStep px vertically.
    const reframeScaledWidth = THUMBSTRIP_WIDTH * origWidth;
    const reframeFrameStep =
      (NATURAL_THUMB_HEIGHT * reframeScaledWidth) / NATURAL_THUMB_WIDTH;
    const reframeYOffset = origY * THUMBSTRIP_HEIGHT;

    const addThumb = (x, y) => {
      const adjustedX = isReframeItem ? x + origX * THUMBSTRIP_WIDTH : x;
      const ax = Math.round(adjustedX * 10) / 10;
      const ay = Math.round(y * 10) / 10;
      if (!thumbStyle.backgroundImage) {
        thumbStyle.backgroundImage = `url(${thumbstripSrc || thumbSrc})`;
      } else {
        thumbStyle.backgroundImage += `,url(${thumbstripSrc || thumbSrc})`;
      }
      if (!thumbStyle.backgroundPosition) {
        thumbStyle.backgroundPosition = `${ax}px ${ay}px`;
      } else {
        thumbStyle.backgroundPosition += `,${ax}px ${ay}px`;
      }
    };
    let sliderMaxWidth = mediaDurationSteps * stepSizePx; // maximum width slider(sliderbg) can take for duration

    // NOTE: update thumb count logic if logic is changed in animaker api repo
    let thumbCount = 150; // number of thumbs in thumbstrip
    const videoStart = item.get("videoStart") || 0;
    const videoEnd = item.get("videoEnd") || 0;
    // Thumbstrip is generated from the ENTIRE original video file, so we need the
    // full source duration to map a time to a frame index.
    // NOTE: some flows (e.g. AI reframe splits) store the CLIP length in
    // videoDuration. Using that would push indices past the end of the strip and
    // render a blank frame. playEnd holds the full source duration, so pick the
    // largest reliable duration signal.
    const fullVideoDuration = Math.max(
      Number(item.get("playEnd")) || 0,
      Number(item.get("videoDuration")) || 0,
      videoEnd
    ); // full original video duration

    if (fullVideoDuration < 300) {
      // To add the thumbstrip to the entire slider If restricted.
      thumbCount = Math.ceil(fullVideoDuration);
      if (thumbCount < 1) {
        thumbCount = 1;
      }
    }

    const enterStart = enterStartStep;
    const exitEnd = exitEndStep;

    const timelineScrollStep = pxToStep2({
      stepSizePx,
      x: timelineScrollX - RULER_OPTIONS.paddingLeft,
    });
    const timelineScrollStep2 =
      timelineScrollStep + pxToStep2({ stepSizePx, x: timelinePlotWidth });

    let startPx = (enterStart - mediaMinStep) * stepSizePx;
    let endPx = sliderMaxWidth - (mediaMaxStep - exitEnd) * stepSizePx;

    let minPx = startPx + (timelineScrollStep - enterStart) * stepSizePx;
    let maxPx = startPx + (timelineScrollStep2 - enterStart) * stepSizePx;

    if (maxThumbCount !== null) {
      thumbCount = maxThumbCount;
      if (isReframeItem) {
        const scaledW = Math.round(THUMBSTRIP_WIDTH * origWidth * 10) / 10;
        thumbStyle.backgroundSize = `${scaledW}px auto`;
      } else {
        thumbStyle.backgroundSize = "contain";
      }
      thumbStyle.minWidth = `${THUMBSTRIP_WIDTH}px`;
      if (enterStartDragStepDiff > 0) {
        // left thumb is being dragged
        thumbStyle.right = 0;
      } else {
        thumbStyle.left = 0;
      }

      if (enterStartDragStepDiff < 0) {
        const startDiff =
          Math.ceil((-enterStartDragStepDiff * stepSizePx) / THUMBSTRIP_WIDTH) *
          THUMBSTRIP_WIDTH;
        startPx += startDiff;
        endPx += startDiff;
        minPx += startDiff;
        maxPx += startDiff;
        sliderMaxWidth += startDiff;
      }
      if (exitEndDragStepDiff > 0) {
        const endDiff =
          Math.ceil((exitEndDragStepDiff * stepSizePx) / THUMBSTRIP_WIDTH) *
          THUMBSTRIP_WIDTH;
        endPx += endDiff;
        maxPx += endDiff;
        sliderMaxWidth += endDiff;
      }
    }

    if (isReframeItem && maxThumbCount === null) {
      // Time-based reframe strip: zoom the sprite so the reframe crop is applied
      // while different frames are revealed per timeline position (below).
      thumbStyle.backgroundSize = `${Math.round(reframeScaledWidth * 10) / 10}px auto`;
    }

    const totalIdealThumbs = Math.max(
      1,
      Math.ceil(sliderMaxWidth / THUMBSTRIP_WIDTH)
    ); // total thumbs required to fill slider
    if (totalIdealThumbs <= thumbCount) {
      const thumbRenderStartIdx = Math.max(
        0,
        Math.floor(totalIdealThumbs * (minPx / sliderMaxWidth)) - 1,
        Math.floor(totalIdealThumbs * (startPx / sliderMaxWidth)) - 1
      );
      const thumbRenderEndIdx = Math.min(
        Math.ceil(totalIdealThumbs * (endPx / sliderMaxWidth)) + 1,
        Math.ceil(totalIdealThumbs * (maxPx / sliderMaxWidth)) + 1,
        totalIdealThumbs - 1
      );

      for (
        let thumbRenderIdx = thumbRenderStartIdx;
        thumbRenderIdx <= thumbRenderEndIdx;
        thumbRenderIdx += 1
      ) {
        const x = THUMBSTRIP_WIDTH * thumbRenderIdx;
        const thumbStep = pxToStep2({ stepSizePx, x });
        const thumbTime = thumbStep * stepperInterval;
        // Apply speed to get the actual video time (not timeline time)
        const videoTime = thumbTime * speed;
        // Add videoStart offset to get the position in the FULL original video
        const absoluteVideoTime = videoStart + videoTime;
        const thumbIdx =
          thumbCount > 1
            ? Math.min(
                thumbCount - 1,
                Math.max(
                  0,
                  Math.floor(thumbCount * (absoluteVideoTime / fullVideoDuration))
                )
              )
            : 0;
        // Reframe strips are zoomed, so step by the scaled frame height.
        const y = isReframeItem
          ? reframeYOffset - reframeFrameStep * thumbIdx
          : THUMBSTRIP_HEIGHT * thumbIdx * -1; // move thumbstrip towards top to reveal one by one
        addThumb(x - startPx, y);
      }
    } else {
      const excessThumbCount = Math.max(0, totalIdealThumbs - thumbCount); // how much thumbs we need
      const excessCountPerThumb = Math.floor(excessThumbCount / thumbCount); // distribute excess to all available thumbs
      const missedExcessCount = Math.max(
        0,
        excessThumbCount - excessCountPerThumb * thumbCount
      ); // correct for excess floor
      const bleedingWidth =
        Math.max(0, sliderMaxWidth - totalIdealThumbs * THUMBSTRIP_WIDTH) +
        missedExcessCount * THUMBSTRIP_WIDTH; // how much bg color of slider will bleed through thumbs at the end

      const totalThumbsRender = thumbCount + excessCountPerThumb * thumbCount; // total thumbs to render on slider

      const bleedingWidthPerThumb = bleedingWidth / totalThumbsRender; // equally distribute bleeding gap to all thumbs
      const thumbWidth = THUMBSTRIP_WIDTH + bleedingWidthPerThumb;

      const thumbRenderStartIdx = Math.max(
        0,
        Math.floor(totalThumbsRender * (minPx / sliderMaxWidth)) - 1,
        Math.floor(totalThumbsRender * (startPx / sliderMaxWidth)) - 1
      );
      const thumbRenderEndIdx = Math.min(
        Math.ceil(totalThumbsRender * (endPx / sliderMaxWidth)) + 1,
        Math.ceil(totalThumbsRender * (maxPx / sliderMaxWidth)) + 1,
        totalThumbsRender - 1
      );

      // chrome and firefox for some reason brings overlapping duplicate thumbs to the top even when they are added before next thumb
      // so add overlapping thumbs when all thumbs are added
      const fillLater = [];

      for (
        let thumbRenderIdx = thumbRenderStartIdx;
        thumbRenderIdx <= thumbRenderEndIdx;
        thumbRenderIdx += 1
      ) {
        const x = thumbWidth * thumbRenderIdx - startPx;

        // Calculate thumbIdx based on TIME position (same as simple path)
        const thumbStep = pxToStep2({ stepSizePx, x: x + startPx });
        const thumbTime = thumbStep * stepperInterval;
        // Apply speed to get the actual video time (not timeline time)
        const videoTime = thumbTime * speed;
        // Add videoStart offset to get the position in the FULL original video
        const absoluteVideoTime = videoStart + videoTime;
        const thumbIdx =
          thumbCount > 1
            ? Math.min(
                thumbCount - 1,
                Math.max(
                  0,
                  Math.floor(thumbCount * (absoluteVideoTime / fullVideoDuration))
                )
              )
            : 0;

        // Reframe strips are zoomed, so step by the scaled frame height.
        const y = isReframeItem
          ? reframeYOffset - reframeFrameStep * thumbIdx
          : THUMBSTRIP_HEIGHT * thumbIdx * -1; // move thumbstrip towards top to reveal one by one
        addThumb(x, y);

        const repeat = Math.ceil(bleedingWidthPerThumb / THUMBSTRIP_WIDTH);
        for (let r = 1; r <= repeat; r += 1) {
          fillLater.push([x + THUMBSTRIP_WIDTH * r, y]);
        }
      }

      fillLater.forEach((fillParams) => addThumb(...fillParams));
    }

    return thumbStyle;
  }, [
    THUMBSTRIP_WIDTH,
    THUMBSTRIP_HEIGHT,
    stepperInterval,
    mediaDurationSteps,
    mediaMinStep,
    enterStartStep,
    exitEndStep,
    mediaMaxStep,
    stepSizePx,
    thumbstripSrc,
    thumbSrc,
    timelineScrollX,
    timelinePlotWidth,
    maxThumbCount,
    enterStartDragStepDiff,
    exitEndDragStepDiff,
    speed,
    item
  ]);

  return <ThumbstripStyled style={thumbStyle} />;
};
ThumbstripComponent.propTypes = {
  stepperInterval: PropTypes.number,
  isDragSlider: PropTypes.bool,
  slider: SliderType,
  sliderHeight: PropTypes.number,
  item: PropTypes.object,
  timelinePlotWidth: PropTypes.number,
  stepSizePx: PropTypes.number,
  thumbstripSrc: PropTypes.string,
  thumbSrc: PropTypes.string,
};

const SliderBG = ({
  slider,
  sliderPlot,
  sliderData,
  trackType,
  stepper,
  isMute,
  isDragSlider,
  isThumbDrag,
  audioWaveColor,
  textColor,
  timelinePlotWidth,
  thumbstripSrc,
  thumbSrc
}) => {
  const appReducer = useSelector((state) => state.app);
  const theme = appReducer.get("theme");
  if (slider?.sliderType === SLIDER_TYPES.TIMELINE_PLACEHOLDER) {
    const overlayText = slider?.payload?.text || "";
    const overlayColors = vmTheme[theme].SLIDER_COLORS?.TIMELINE_PLACEHOLDER?.default;
    return (
      <Typography
        textAlign="left"
        font={font.normalMini_12}
        color={textColor || overlayColors?.text || vmTheme[theme].subtitleTextColor}
        content={overlayText}
        enableTrim
      />
    );
  }
  const [text, setText] = useState("");
  const data = useSelector((store) =>
    store.projectDetails.getIn(["workspaceItems", slider.id])
  );
  const { sliderType } = slider;
  const cleanAudioData = appReducer.get("cleanAudio");
  const isAudioCleaning = cleanAudioData?.get("isAudioCleaning") && sliderData?.get("id") === cleanAudioData.get("id");

  useEffect(() => {
    if (sliderData) {
      if (sliderType === SLIDER_TYPES.SUBTITLE) {
        setText(sliderData.get("text"));
      }
      if (isTextOnly(sliderData.get("type"))) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = data?.getIn(["textData", "htmlText"]);
        setText(tempDiv.textContent || tempDiv.innerText || "");
      }
      if (sliderData.get("type") === "SHAPE") {
        let selectedShapeName = sliderData.getIn(["pathData", "type"]) || "";

        if (typeof selectedShapeName === "string") {
          selectedShapeName = selectedShapeName.replace(/_/g, " ");
          selectedShapeName =
            selectedShapeName.charAt(0).toUpperCase() + selectedShapeName.substr(1);
        }

        setText(selectedShapeName);
      }
      if (sliderData.get("type") === "PROP") {
        setText("Properties");
      }
      if (sliderData.get("type") === "FRAME") {
        setText("Frame");
      }
    }
  }, [data, sliderData, sliderType]);


  if (sliderType === SLIDER_TYPES.MINI_SUBTITLE || sliderType === SLIDER_TYPES.MINI_SMART_FOCUS || sliderType === SLIDER_TYPES.MINI_REFRAME) {
    const sliderText = sliderType === SLIDER_TYPES.MINI_SMART_FOCUS ? "Smart focus" : sliderType === SLIDER_TYPES.MINI_REFRAME  ? "AI Reframe" : "Subtitles"
    return (
      <BgContainer>
        <TextContainer className={`slider-bg--tc-${sliderType}`}>
          <img
            src={sliderType === SLIDER_TYPES.MINI_SMART_FOCUS || sliderType === SLIDER_TYPES.MINI_REFRAME  ? `${STATIC_PATH}${vmTheme[theme].icons.smartFocusIcon}`:`${STATIC_PATH}${vmTheme[theme].icons.subtitleIconSlider}`}
            alt="Subtitles"
            width="12px"
            height="12px"
          />
          <Typography
            textAlign="left"
            font={font.normalMicro}
            color={textColor}
            innerContent= {sliderText}
            enableTrim
          />
        </TextContainer>
      </BgContainer>
    );
  }

  if (sliderData) {
    if (sliderType === SLIDER_TYPES.SUBTITLE) {
      return (
        <BgContainer>
          <TextContainer>
            <Typography
              textAlign="left"
              font={font.normalMini_12}
              color={vmTheme[theme].polarColor}
              innerContent={text}
              enableTrim
            />
          </TextContainer>
        </BgContainer>
      );
    }

    if (sliderType === SLIDER_TYPES.SMART_FOCUS) {
      return (
        <BgContainer>
          <TextContainer>
          <img
            src={`${STATIC_PATH}${vmTheme[theme].icons.smartFocusIcon}`}
            alt="Subtitles"
            width="20px"
            height="20px"
          />
            <Typography
              textAlign="left"
              font={font.normalMini_12}
              color={vmTheme[theme].smartFocusTextColor}
              innerContent={slider.zoomName}
              enableTrim
            />
          </TextContainer>
        </BgContainer>
      );
    }

    if (
      isTextOnly(sliderData.get("type")) ||
      sliderData.get("type") === "SHAPE" ||
      sliderData.get("type") === "PROP" ||
      sliderData.get("type") === "FRAME"
    ) {
      const isShape =
        sliderData.get("type") === "SHAPE" || sliderData.get("type") === "PROP" || sliderData.get("type") === "FRAME";
      return (
        <BgContainer>
          <TextContainer isShape={isShape}>
            <img
              src={`${STATIC_PATH}${isShape ? vmTheme[theme].icons.shapeIcon : vmTheme[theme].icons.textTimelineIcon}`}
              alt="T"
              width="20px"
              height="20px"
            />
            <Text
              textAlign="left"
              font={font.normalMini_12}
              color={
                isShape
                  ? vmTheme[theme].shapeColor
                  : vmTheme[theme].sliderTextContainerColor
              }
            >
              {text}
            </Text>
          </TextContainer>
        </BgContainer>
      );
    }
    if (
      isVideoOnly(sliderData.get("type"), sliderData.get("subType")) &&
      sliderData.get("type") !== "PEXELS" &&
      sliderData.get("type") !== "PIXABAY" &&
      !(
        sliderData.get("type") === "STOCKVID" &&
        sliderData.get("thumb")?.startsWith("https://")
      ) &&
      sliderType !== SLIDER_TYPES.AUDIO
    ) {
      // If video and is not form PEXELS and PIXABAY.
      return (
        <BgContainer>
          {trackType === "video" && <ThumbWrapper1 />}
          <ThumbstripComponent
            timelinePlotWidth={timelinePlotWidth}
            slider={slider}
            sliderHeight={sliderPlot.height}
            stepSizePx={stepper.stepSizePx}
            stepperInterval={stepper.interval}
            item={sliderData}
            isDragSlider={isDragSlider}
            thumbstripSrc={thumbstripSrc}
            thumbSrc={thumbSrc}
          />
          {trackType === "video" && <ThumbWrapper2 />}
          {isAudioCleaning &&
            <CleaningAudioLoader
              sliderPlot={sliderPlot}
              timelinePlotWidth={timelinePlotWidth}
            />}
        </BgContainer>
      );
    }
    if (
      isImageOnly(sliderData.get("type"), sliderData.get("subType")) ||
      sliderData.get("type") === "PEXELS" ||
      sliderData.get("type") === "PIXABAY" ||
      (sliderData.get("type") === "STOCKVID" && sliderData.get("thumb")?.startsWith("https://"))
    ) {
      // If image and videos form PEXELS and PIXABAY.
      return (
        <BgContainer>
          <ThumbstripComponent
            timelinePlotWidth={timelinePlotWidth}
            slider={slider}
            sliderHeight={sliderPlot.height}
            stepSizePx={stepper.stepSizePx}
            stepperInterval={stepper.interval}
            item={sliderData}
            isDragSlider={isDragSlider}
            thumbstripSrc={thumbstripSrc}
            thumbSrc={thumbSrc}
          />
        </BgContainer>
      );
    }

    if (sliderType === SLIDER_TYPES.AUDIO && !isMute) {
      return (
        <>
          <WaveForm
            isDragSlider={isDragSlider}
            isThumbDrag={isThumbDrag}
            audioWaveColor={audioWaveColor}
            slider={slider}
            sliderHeight={sliderPlot.height}
            sliderData={sliderData}
            stepSizePx={stepper.stepSizePx}
          />
          {isAudioCleaning &&
            <CleaningAudioLoader
              isAudio
              sliderPlot={sliderPlot}
              timelinePlotWidth={timelinePlotWidth}
            />}
        </>
      );
    }
  }
  return null;
};

SliderBG.propTypes = {
  slider: SliderType.isRequired,
  sliderPlot: PropTypes.object,
  sliderData: PropTypes.object,
  trackType: PropTypes.string,
  stepper: StepperType,
  isMute: PropTypes.bool,
  isDragSlider: PropTypes.bool,
  isThumbDrag: PropTypes.bool,
  audioWaveColor: PropTypes.string,
  textColor: PropTypes.string,
  timelinePlotWidth: PropTypes.number,
  thumbstripSrc: PropTypes.string,
  thumbSrc: PropTypes.string,
};

export default SliderBG;
